// Client for the public Panther API. The terminal reads exactly what any API
// caller gets, so the public API is always in real use.

import type { FlowFields } from "./flows";

/** True in the dev server with `?fixture` in the address; never in a build or in tests. */
export function wantsFixture(): boolean {
  return import.meta.env.DEV && import.meta.env.MODE !== "test" && typeof location !== "undefined" && new URLSearchParams(location.search).has("fixture");
}

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.pantherterminal.com";

export interface BoardDeployment {
  chain: string;
  address: string;
  supply: number | null;
  /** Holders on this chain from the chain's explorer, where the venue's board reads them. */
  explorer_holders?: number | null;
}

/** Net flow fields (C-6c); see flows.ts. */
export interface BoardToken extends FlowFields {
  id: string;
  /** Who issues the token (C-7b), e.g. Robinhood for its own stock tokens; absent on older boards. */
  issuer?: { id: string; name: string } | null;
  symbol: string;
  name: string | null;
  chain: string;
  /** Every chain the token is deployed on with supply. */
  chains: string[];
  address: string;
  /** Per-chain contracts and supplies, where the venue reports them. */
  deployments?: BoardDeployment[] | null;
  asset_id: string | null;
  isin: string | null;
  logo_url: string | null;
  price: number | null;
  price_basis: string | null;
  price_provider: string | null;
  /** Why the published price is not the venue's own quote (e.g. an xStocks issuer quote over 3% off the listed price); null or absent otherwise. */
  price_note?: string | null;
  bid: number | null;
  ask: number | null;
  underlying_price: number | null;
  multiplier: number | null;
  price_at: string | null;
  supply: number | null;
  market_cap: number | null;
  holders: number | null;
  volume_usd: number | null;
  mint_burn_usd: number | null;
  transfers_24h: number | null;
  halted: boolean | null;
  change_24h: { price: number | null; market_cap: number | null; supply: number | null };
}

/** Share of a group a summed total measures. A total is published only at 90% or more. */
export interface Coverage {
  measured_tokens: number;
  /** Share (0–1) of the group's value held by the measured tokens; null for an empty group. */
  measured_cap_share: number | null;
}

export type CoverageMetric = "market_cap" | "holders" | "volume_usd" | "mint_burn_usd" | "transfers_24h";

export interface Board {
  schema: "panther.board.v1";
  venue: { id: string; name: string; kind: string; url: string | null; chains: string | null };
  slot: number;
  as_of: string;
  generated_at: string;
  totals: FlowFields & {
    tokens: number;
    priced: number;
    market_cap: number | null;
    volume_usd: number | null;
    mint_burn_usd: number | null;
    transfers_24h: number | null;
    holders: number | null;
    /** How much of the board's value each total measures (D-2). Absent on older boards. */
    coverage?: Partial<Record<CoverageMetric, Coverage>>;
  };
  tokens: BoardToken[];
}

export async function fetchBoard(venue: string, signal?: AbortSignal): Promise<Board> {
  const response = await fetch(`${API_BASE}/v1/venues/${venue}/board`, { signal });
  if (response.status === 404) throw new NotPublished();
  if (!response.ok) throw new Error(`The API answered ${response.status}`);
  const board = (await response.json()) as Board;
  if (board.schema !== "panther.board.v1") throw new Error("Unexpected board format");
  return board;
}

/** The venue has no board yet: its first collection has not finished. */
export class NotPublished extends Error {
  constructor() {
    super("First collection in progress");
  }
}

const EXPLORERS: Record<string, string> = {
  robinhood: "https://robinhoodchain.blockscout.com/token/",
  ethereum: "https://etherscan.io/token/",
  base: "https://basescan.org/token/",
  arbitrum: "https://arbiscan.io/token/",
  bsc: "https://bscscan.com/token/",
  avalanche: "https://snowtrace.io/token/",
  morph: "https://explorer.morphl2.io/token/",
  solana: "https://solscan.io/token/",
  stellar: "https://stellar.expert/explorer/public/asset/",
  algorand: "https://allo.info/asset/",
  provenance: "https://explorer.provenance.io/asset/",
};

/**
 * Display names for every chain id the collector can emit: the venue boards'
 * chains, the class boards' registry chains (packages/registry/data) and the
 * collector's KNOWN_CHAINS. Read through chainLabel(), which title-cases any
 * id missing here.
 */
export const CHAIN_LABELS: Record<string, string> = {
  robinhood: "Robinhood",
  ethereum: "Ethereum",
  base: "Base",
  arbitrum: "Arbitrum",
  bsc: "BNB",
  avalanche: "Avalanche",
  morph: "Morph",
  solana: "Solana",
  stellar: "Stellar",
  algorand: "Algorand",
  provenance: "Provenance",
  polygon: "Polygon",
  optimism: "Optimism",
  aptos: "Aptos",
  mantle: "Mantle",
  sui: "Sui",
  plume: "Plume",
  celo: "Celo",
  gnosis: "Gnosis",
  xrpl: "XRP Ledger",
  near: "NEAR",
  tron: "Tron",
  canton: "Canton",
  hedera: "Hedera",
  injective: "Injective",
  noble: "Noble",
  zksync: "ZKsync",
  linea: "Linea",
  scroll: "Scroll",
  sonic: "Sonic",
  berachain: "Berachain",
  ink: "Ink",
  arc: "Arc",
  etherlink: "Etherlink",
  monad: "Monad",
  "oasis-sapphire": "Oasis Sapphire",
  pharos: "Pharos",
  rootstock: "Rootstock",
  sei: "Sei",
  starknet: "Starknet",
  tempo: "Tempo",
  xlayer: "X Layer",
  hyperevm: "HyperEVM",
  xdc: "XDC Network",
  "kinesis-kau": "Kinesis KAU",
  "kinesis-kag": "Kinesis KAG",
  liquid: "Liquid Network",
  offchain: "Off-chain",
};

/** "oasis-sapphire" -> "Oasis Sapphire": the fallback for a chain id with no entry. */
export function titleCaseChain(chain: string): string {
  return chain
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Display name for a chain id; an unknown id is title-cased, never shown raw. */
export const chainLabel = (chain: string): string =>
  Object.hasOwn(CHAIN_LABELS, chain) ? CHAIN_LABELS[chain]! : titleCaseChain(chain) || chain;

export const explorerUrl = (chain: string, address: string) => (EXPLORERS[chain] ? EXPLORERS[chain] + address : null);
